import React from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  HStack,
  Input,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  UnorderedList,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { parseRiskReviewTemplateImport } from "../../utils/riskReviewTemplateImportParser";

const RiskReviewBulkUploadTemplateModal = ({ isOpen, onClose, onImport, isImporting }) => {
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const [fileName, setFileName] = React.useState("");
  const [parsed, setParsed] = React.useState(null);
  const [parseError, setParseError] = React.useState("");
  const [isParsing, setIsParsing] = React.useState(false);

  React.useEffect(() => {
    if (!isOpen) {
      setFileName("");
      setParsed(null);
      setParseError("");
    }
  }, [isOpen]);

  const handleFile = async (file) => {
    if (!file) return;
    setFileName(file.name);
    setParseError("");
    setParsed(null);
    setIsParsing(true);
    try {
      const result = await parseRiskReviewTemplateImport(file);
      setParsed(result);
    } catch (error) {
      setParseError(error?.message || "Unable to parse template import file.");
    } finally {
      setIsParsing(false);
    }
  };

  const preview = parsed?.preview;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Bulk Upload Template</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" p={4}>
              <Text fontWeight="medium" mb={2}>
                Upload Excel / JSON template definition
              </Text>
              <Input
                type="file"
                accept=".json,.xlsx,.xls"
                onChange={(event) => handleFile(event.target.files?.[0])}
              />
              {fileName ? (
                <Text mt={2} color="gray.500" fontSize="sm">
                  {fileName}
                </Text>
              ) : null}
            </Box>

            {parseError ? (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                {parseError}
              </Alert>
            ) : null}

            {preview ? (
              <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" p={4}>
                <Text fontWeight="semibold" mb={3}>
                  Preview Import
                </Text>
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                  <Stat>
                    <StatLabel>Sections</StatLabel>
                    <StatNumber>{preview.sections}</StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel>Fields</StatLabel>
                    <StatNumber>{preview.fields}</StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel>Questions</StatLabel>
                    <StatNumber>{preview.questions}</StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel>Risk Triggers</StatLabel>
                    <StatNumber>{preview.riskTriggers}</StatNumber>
                  </Stat>
                </SimpleGrid>
                {preview.warnings.length ? (
                  <Alert status="warning" borderRadius="md" mt={4} alignItems="flex-start">
                    <AlertIcon />
                    <Box>
                      <Text fontWeight="medium">Validation warnings</Text>
                      <UnorderedList mt={2}>
                        {preview.warnings.map((warning) => (
                          <ListItem key={warning}>{warning}</ListItem>
                        ))}
                      </UnorderedList>
                    </Box>
                  </Alert>
                ) : null}
                {preview.errors.length ? (
                  <Alert status="error" borderRadius="md" mt={4} alignItems="flex-start">
                    <AlertIcon />
                    <Box>
                      <Text fontWeight="medium">Validation errors</Text>
                      <UnorderedList mt={2}>
                        {preview.errors.map((error) => (
                          <ListItem key={error}>{error}</ListItem>
                        ))}
                      </UnorderedList>
                    </Box>
                  </Alert>
                ) : null}
              </Box>
            ) : null}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              isLoading={isParsing || isImporting}
              isDisabled={!parsed || preview?.errors?.length}
              onClick={() => onImport?.(parsed.template)}
            >
              Import as Draft
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RiskReviewBulkUploadTemplateModal;
